import { AppConfig, usePermissions } from '@ltvco/refresh-lib/ctx';
import { Box, Button, Stack } from '@ltvco/refresh-lib/theme';
import { OnSuccessCallback, SearchData } from '@ltvco/refresh-lib/v1';
import {
  HeaderMenu,
  ChatAICTAButton,
  NotificationBell,
} from '@ltvco/refresh-lib/v2';
import { AccountCircle, Search } from '@mui/icons-material';
import { useCallback, useContext, useState, type MouseEvent } from 'react';

import { useHeaderContext } from 'context/HeaderContext';
import { useHandleSearch } from 'hooks/useHandleSearch';

import { ClassicHeaderItem } from '../ClassicHeaderItem/ClassicHeaderItem';

import { HeaderMenuDropdown } from './HeaderMenuDropdown/HeaderMenuDropdown';
import { HeaderSearchDropdown } from './HeaderSearchDropdown/HeaderSearchDropdown';
import { HeaderSimpleMenu } from './HeaderSimpleMenu/HeaderSimpleMenu';

import type { HeaderItem, DropdownHeaderItemName } from '../types';

interface DesktopHeaderNavProps {
  handleNotificationBellClick: (event: MouseEvent<HTMLButtonElement>) => void;
  unreadNotificationCount: number;
}

export const DesktopHeaderNav = ({
  handleNotificationBellClick,
  unreadNotificationCount,
}: DesktopHeaderNavProps) => {
  const { currentDropdown, handleSetCurrentDropdown } = useHeaderContext();
  const [anchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { handleSearchSuccess } = useHandleSearch();
  const { isFreePlan } = usePermissions();

  const handleClose = useCallback(() => {
    handleSetCurrentDropdown(null);
  }, [handleSetCurrentDropdown]);

  const onSearchSuccess: OnSuccessCallback = (searchPayload: SearchData) => {
    handleSearchSuccess && handleSearchSuccess(searchPayload);
    handleSetCurrentDropdown(null);
  };

  const headerItems: HeaderItem[] = [
    {
      visibleText: 'History Reports',
      prefixIcon: <Search />,
      componentToDisplayWhenOpen: (
        <HeaderSearchDropdown
          name={'search'}
          isFreePlan={isFreePlan}
          onSearchSuccess={onSearchSuccess}
          id="search-dropdown"
          handleClose={handleClose}
          anchorEl={anchorEl}
          setMenuAnchorEl={setMenuAnchorEl}
          placement={'header'}
        />
      ),
      eventName: 'openVehicleSearch',
      name: 'search',
      eventLabel: 'Search',
    },
    {
      visibleText: 'Marketplace',
      componentToDisplayWhenOpen: (
        <HeaderSimpleMenu
          name="marketplace"
          id="marketplace-dropdown"
          anchorEl={anchorEl}
          handleClose={handleClose}
          listItems={[
            {
              url: '/marketplace',
              visibleText: 'Buy',
              openInNewTab: false,
              eventLabel: 'Marketplace',
            },
            {
              url: '/marketplace/sell-your-car',
              visibleText: 'Sell',
              openInNewTab: false,
              eventLabel: 'Marketplace',
            },
            {
              url: 'https://www.bumper.com/tools/compare-cars/',
              visibleText: 'Compare vehicles',
              openInNewTab: true,
              eventLabel: 'Marketplace',
            },
            {
              url: 'https://www.bumper.com/tools/market-value/',
              visibleText: 'Market value',
              openInNewTab: true,
              eventLabel: 'Marketplace',
            },
            {
              url: `/marketplace/listed-vehicles`,
              visibleText: 'Listed Vehicles',
              openInNewTab: true,
              eventLabel: 'Marketplace',
            },
            {
              url: '/marketplace/showroom',
              visibleText: 'Saved Vehicles',
              openInNewTab: false,
              eventLabel: 'Marketplace',
            },
            {
              url: '/marketplace/saved-searches',
              visibleText: 'Saved Searches',
              openInNewTab: false,
              eventLabel: 'Marketplace',
            },
          ]}
        />
      ),
      eventName: 'openMarketplaceDropdown',
      name: 'marketplace',
      eventLabel: 'Marketplace',
    },
    {
      visibleText: 'My Bumper',
      componentToDisplayWhenOpen: (
        <HeaderSimpleMenu
          name="myBumper"
          id="my-bumper-dropdown"
          anchorEl={anchorEl}
          handleClose={handleClose}
          listItems={[
            {
              url: '/dashboard/home',
              visibleText: 'My Garage',
              openInNewTab: false,
              eventLabel: 'My Bumper',
            },
            {
              url: '/dashboard/rewards',
              visibleText: 'Rewards',
              openInNewTab: false,
              eventLabel: 'My Bumper',
            },
            {
              url: '/dashboard/community-buzz',
              visibleText: 'Community Buzz',
              openInNewTab: false,
              eventLabel: 'My Bumper',
            },
            {
              url: 'https://www.bumper.com/blog/',
              visibleText: 'Helpful articles',
              openInNewTab: true,
              eventLabel: 'My Bumper',
            },
          ]}
        />
      ),
      eventName: 'openMyBumperDropdown',
      name: 'myBumper',
      eventLabel: 'My Bumper',
    },
    {
      icon: <AccountCircle sx={{ color: '#212121' }} />,
      componentToDisplayWhenOpen: (
        <HeaderMenuDropdown
          name="menu"
          handleClose={handleClose}
          id="header-menu"
        >
          <HeaderMenu
            open={currentDropdown === 'menu'}
            handleClose={handleClose}
            showCustomSideMenu
            customStyle={{
              minWidth: '284px',
            }}
          />
        </HeaderMenuDropdown>
      ),
      eventName: 'openMenuDropdown',
      name: 'menu',
      eventLabel: 'Avatar',
    },
  ];

  return (
    <Stack direction={'row'}>
      {headerItems.slice(0, headerItems.length - 1).map((headerItem, index) => (
        <NewComponent
          item={headerItem}
          setMenuAnchorEl={setMenuAnchorEl}
          key={index}
        />
      ))}
      <ChatAICTAButton name="Bumper AI" />
      <Button
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
        disableRipple
        onClick={handleNotificationBellClick}
      >
        <NotificationBell unreadCount={unreadNotificationCount} />
      </Button>
      <NewComponent
        item={headerItems[headerItems.length - 1]}
        setMenuAnchorEl={setMenuAnchorEl}
        key={headerItems.length - 1}
      />
    </Stack>
  );
};

interface NewComponentProps {
  item: HeaderItem;
  setMenuAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
}

const NewComponent = ({ item, setMenuAnchorEl }: NewComponentProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const { currentDropdown, handleSetCurrentDropdown } = useHeaderContext();

  const onClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    trackEventGA4({
      event_name: 'header_engagement',
      type: `nav - ${item.eventLabel}`.toLowerCase(),
    });
    if (item.eventName) {
      if (!currentDropdown || currentDropdown !== item.name) {
        setMenuAnchorEl(event.currentTarget);
        handleSetCurrentDropdown(item.name as DropdownHeaderItemName);
      } else {
        setMenuAnchorEl(null);
        handleSetCurrentDropdown(null);
      }
    }
  };

  return (
    <Box id={`header-nav-item-${item.eventLabel}`}>
      <ClassicHeaderItem item={item} onClickHandler={onClickHandler} />
      {item.componentToDisplayWhenOpen}
    </Box>
  );
};
